import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../../../axiosConfig';
import dayjs from 'dayjs';
import i18n from '../../../../i18n';
import { CustomDateTimePicker } from '../../../commons/datepicker';
import "../../../../styles/EditTrainingLockModal.css";
import DeleteConfirmationModal from '../../DeleteConfirmationModal'; // Add this import

const EditTrainingLockModal = ({ isOpen, onClose, lock, refreshCalendar, onDelete }) => {
    const { t } = useTranslation(['calendar', 'common']);
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // NEW: State for delete confirmation modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    // Initialize form with lock data
    useEffect(() => {
        if (isOpen && lock) {
            const start = dayjs(lock.startTime);
            const end = dayjs(lock.endTime);

            // Format for datetime-local input
            setStartTime(start.format('YYYY-MM-DDTHH:mm'));
            setEndTime(end.format('YYYY-MM-DDTHH:mm'));
            setError('');
        }
    }, [isOpen, lock]);

    const handleSave = async () => {
        // Validation
        const start = dayjs(startTime);
        const end = dayjs(endTime);

        if (!start.isValid() || !end.isValid()) {
            setError(t('lock.invalidTimeFormat'));
            return;
        }

        if (end.isBefore(start) || end.isSame(start)) {
            setError(t('lock.endBeforeStart'));
            return;
        }

        // Minimum 15 minutes duration
        if (end.diff(start, 'minute') < 15) {
            setError(t('lock.minDuration'));
            return;
        }

        setLoading(true);
        const token = localStorage.getItem("jwt");

        const requestBody = {
            lockId: lock.id,
            trainingLockTime: {
                // FIXED: Use ISO format with 'T' separator instead of space
                startTime: start.format('YYYY-MM-DDTHH:mm:ss'),
                endTime: end.format('YYYY-MM-DDTHH:mm:ss')
            }
        };

        try {
            await axios.put('/api/v1/training/lock', requestBody, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert(t('lock.lockUpdatedSuccess'));
            refreshCalendar();
            onClose();
        } catch (err) {
            setError(t('lock.lockUpdateError'));
            console.error('Error updating lock:', err);
        } finally {
            setLoading(false);
        }
    };

    // NEW: Handle opening delete confirmation modal
    const handleDeleteClick = () => {
        setDeleteModalOpen(true);
    };

    // NEW: Handle confirmed deletion
    const handleConfirmDelete = () => {
        onDelete(lock);
        setDeleteModalOpen(false);
        onClose();
    };

    // NEW: Handle cancel deletion
    const handleCancelDelete = () => {
        setDeleteModalOpen(false);
    };

    const handleStartTimeChange = (e) => {
        const newStartTime = e.target.value;
        setStartTime(newStartTime);
        setError('');

        // Auto-adjust end time if it's before or equal to start time
        const start = dayjs(newStartTime);
        const end = dayjs(endTime);

        if (end.isBefore(start) || end.isSame(start)) {
            // Snap to next 15-minute interval
            const snappedMinutes = Math.ceil(start.minute() / 15) * 15;
            const snappedEndTime = start.minute(snappedMinutes).second(0);
            setEndTime(snappedEndTime.format('YYYY-MM-DDTHH:mm'));
        }
    };

    const handleEndTimeChange = (e) => {
        setEndTime(e.target.value);
        setError('');
    };

    const handleClose = () => {
        setError('');
        onClose();
    };

    if (!isOpen || !lock) return null;

    const lockDate = dayjs(lock.startTime).locale(i18n.language === 'pl' ? 'pl' : 'en').format('dddd, DD MMMM YYYY');
    const duration = startTime && endTime ? dayjs(endTime).diff(dayjs(startTime), 'minute') : 0;

    return (
        <>
            <div className="edit-lock-modal-overlay">
                <div className="edit-lock-modal-content">
                    <div className="edit-lock-modal-header">
                        <div className="edit-lock-modal-header-info">
                            <h2 className="edit-lock-modal-title">{t('lock.editLock')}</h2>
                            <div className="edit-lock-date">{lockDate}</div>
                        </div>
                        <button className="edit-lock-close-btn" onClick={handleClose}>✕</button>
                    </div>

                    <div className="edit-lock-modal-body">
                        {error && (
                            <div className="edit-lock-error-message">
                                {error}
                            </div>
                        )}

                        <div className="edit-lock-form-group">
                            <label htmlFor="start-time" className="edit-lock-label">
                                {t('lock.startTime')}
                            </label>
                            <CustomDateTimePicker
                                id="start-time"
                                name="startTime"
                                value={startTime}
                                onChange={handleStartTimeChange}
                                disabled={loading}
                                step={900}
                            />
                        </div>

                        <div className="edit-lock-form-group">
                            <label htmlFor="end-time" className="edit-lock-label">
                                {t('lock.endTime')}
                            </label>
                            <CustomDateTimePicker
                                id="end-time"
                                name="endTime"
                                value={endTime}
                                onChange={handleEndTimeChange}
                                disabled={loading}
                                min={startTime}
                                step={900}
                            />
                        </div>

                        {/* Quick duration buttons */}
                        {startTime && (
                            <div className="edit-lock-quick-durations">
                                <label className="edit-lock-label">{t('lock.quickDuration')}</label>
                                <div className="edit-lock-quick-duration-buttons">
                                    <button
                                        type="button"
                                        className="edit-lock-quick-duration-btn"
                                        onClick={() => setEndTime(dayjs(startTime).add(15, 'minute').format('YYYY-MM-DDTHH:mm'))}
                                        disabled={loading}
                                    >
                                        15 min
                                    </button>
                                    <button
                                        type="button"
                                        className="edit-lock-quick-duration-btn"
                                        onClick={() => setEndTime(dayjs(startTime).add(30, 'minute').format('YYYY-MM-DDTHH:mm'))}
                                        disabled={loading}
                                    >
                                        30 min
                                    </button>
                                    <button
                                        type="button"
                                        className="edit-lock-quick-duration-btn"
                                        onClick={() => setEndTime(dayjs(startTime).add(60, 'minute').format('YYYY-MM-DDTHH:mm'))}
                                        disabled={loading}
                                    >
                                        {t('lock.oneHour')}
                                    </button>
                                    <button
                                        type="button"
                                        className="edit-lock-quick-duration-btn"
                                        onClick={() => setEndTime(dayjs(startTime).add(120, 'minute').format('YYYY-MM-DDTHH:mm'))}
                                        disabled={loading}
                                    >
                                        {t('lock.twoHours')}
                                    </button>
                                </div>
                            </div>
                        )}

                        {duration > 0 && (
                            <div className="edit-lock-duration-info">
                                {t('lock.durationMinutes', { minutes: duration })}
                            </div>
                        )}
                    </div>

                    <div className="edit-lock-modal-footer">
                        <button
                            className="edit-lock-delete-btn"
                            onClick={handleDeleteClick}
                            disabled={loading}
                        >
                            {t('lock.deleteLock')}
                        </button>
                        <div className="edit-lock-action-buttons">
                            <button
                                className="edit-lock-cancel-btn"
                                onClick={handleClose}
                                disabled={loading}
                            >
                                {t('common:cancel')}
                            </button>
                            <button
                                className="edit-lock-save-btn"
                                onClick={handleSave}
                                disabled={loading || !startTime || !endTime}
                            >
                                {loading ? t('common:saving') : t('lock.saveChanges')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* NEW: Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={deleteModalOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                itemName={t('lock.lockItemName')}
            />
        </>
    );
};

export default EditTrainingLockModal;